import React, { useState, useMemo, useEffect } from 'react';
import { DentalSeverityBadge } from '../DentalSeverityBadge';
import { STATUS_CONFIGS, isSentinelSeverity } from '../../lib/riskConstants';

/**
 * Accessible Data Table with Sticky Header, Sentinel Row Tint, and Pagination
 */
export default function RiskDataTable({
  reports = [],
  onSelectCase,
  isMobile = false
}) {
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to first page when reports count changes or pageSize changes
  useEffect(() => {
    setCurrentPage(1);
  }, [reports.length, pageSize]);

  const totalPages = Math.max(1, Math.ceil(reports.length / pageSize));

  const paginatedReports = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return reports.slice(start, start + pageSize);
  }, [reports, currentPage, pageSize]);

  return (
    <div className="risk-table-container">
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table className="risk-table">
          <thead>
            <tr>
              <th>รหัสเคส</th>
              <th>วัน-เวลาเกิดเหตุ</th>
              <th>สถานที่</th>
              <th>ความรุนแรง</th>
              <th>หัวข้ออุบัติการณ์</th>
              <th>ผู้ป่วย (HN)</th>
              <th>ผู้รายงาน</th>
              <th>สถานะ</th>
              <th style={{ textAlign: 'center' }}>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {paginatedReports.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '36px', color: '#94a3b8' }}>
                  ไม่พบข้อมูลรายงานความเสี่ยง
                </td>
              </tr>
            ) : (
              paginatedReports.map((r, idx) => {
                const statusCfg = STATUS_CONFIGS[r.status] || STATUS_CONFIGS.pending;
                const isSentinel = isSentinelSeverity(r.severity_level);

                return (
                  <tr
                    key={r.id || r.incident_number}
                    className={`risk-table-row ${isSentinel ? 'risk-table-row--sentinel' : ''}`}
                    onClick={() => onSelectCase?.(r)}
                  >
                    <td style={{ fontWeight: '700', color: '#0284c7', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>{r.incident_number}</span>
                        {r.recall_batch_triggered && (
                          <span
                            style={{
                              fontSize: '0.68em',
                              fontWeight: '800',
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              padding: '1px 5px',
                              borderRadius: '4px'
                            }}
                          >
                            RECALL
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                      {r.incident_date
                        ? new Date(r.incident_date).toLocaleString('th-TH', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })
                        : '-'}
                    </td>
                    <td style={{ color: '#334155', fontWeight: '500', whiteSpace: 'nowrap' }}>
                      {r.location}
                    </td>
                    <td>
                      <DentalSeverityBadge level={r.severity_level} domain={r.risk_domain} size="sm" />
                    </td>
                    <td style={{ fontWeight: '600', color: '#0f172a', maxWidth: '300px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {r.risk_domain === 'cssd' && (
                          <span
                            style={{
                              fontSize: '0.7em',
                              fontWeight: '700',
                              backgroundColor: '#ccfbf1',
                              color: '#0f766e',
                              padding: '1px 5px',
                              borderRadius: '4px',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            🧼 CSSD
                          </span>
                        )}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.title}
                        </span>
                      </div>
                    </td>
                    <td style={{ color: '#475569', whiteSpace: 'nowrap' }}>
                      {r.is_patient_involved ? (
                        <span style={{ color: '#0369a1', fontWeight: '600' }}>
                          HN {r.patient_hn || 'ระบุ'}
                        </span>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>-</span>
                      )}
                    </td>
                    <td style={{ color: '#475569', whiteSpace: 'nowrap' }}>
                      {r.is_anonymous ? (
                        <span style={{ color: '#ef4444', fontWeight: '600', fontSize: '0.9em' }}>🔒 นิรนาม</span>
                      ) : (
                        r.reporter_name || '-'
                      )}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '3px 8px',
                          borderRadius: '9999px',
                          backgroundColor: statusCfg.bg,
                          color: statusCfg.color,
                          border: `1px solid ${statusCfg.border}`,
                          fontSize: '0.82em',
                          fontWeight: '700'
                        }}
                      >
                        <span>{statusCfg.icon}</span>
                        <span>{statusCfg.label}</span>
                      </span>
                    </td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectCase?.(r);
                        }}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          backgroundColor: '#f1f5f9',
                          border: '1px solid #cbd5e1',
                          color: '#334155',
                          fontWeight: '600',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        ดูรายละเอียด
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Bar */}
      <div className="risk-pagination">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span>
            แสดง {reports.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} ถึง{' '}
            {Math.min(currentPage * pageSize, reports.length)} จาก {reports.length} รายการ
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>แถวต่อหน้า:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              style={{
                padding: '3px 8px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '0.85em',
                backgroundColor: '#ffffff'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            className="risk-pagination-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            ← ก่อนหน้า
          </button>
          <span>
            หน้า <strong>{currentPage}</strong> จาก <strong>{totalPages}</strong>
          </span>
          <button
            type="button"
            className="risk-pagination-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            ถัดไป →
          </button>
        </div>
      </div>
    </div>
  );
}
